import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Switch, Tooltip } from "@mui/material";
import { GridColDef, GridSortModel } from "@mui/x-data-grid";

import CustomModal from "../../Components/Shared/ModalCustom/CustomModal";
import ConfirmModal from "../../Components/Shared/ModalCustom/ConfirmModal";
import { DEFAULT_PAGE_SIZE, ListEditItems } from "../../Components/ListEditItems/ListEditItems";
import TableFilters, { FilterValues } from "../../Components/Shared/TableFilters/TableFilters";
import { selectFilter } from "../../Components/Shared/TableFilters/filterDefs";
import { actionsColumn, alignLeft } from "../../Components/Shared/TableActions/TableActions";
import { dateColumn, idColumn, nameColumn, textColumn } from "../../Components/Shared/TableColumns/tableColumns";
import { useApiData } from "../../Hooks/useApiData";
import { useDebounce } from "../../Hooks/useDebounce";
import {
  deleteEmployee,
  EmployeeOrderBy,
  getEmployees,
  setEmployeeActive,
} from "../../Service/EmployeeService/EmployeeService";
import { getRoles } from "../../Service/RolService/RolService";
import { apiErrorStatus, handleApiError } from "../../Utils/apiErrors";
import UserDetail from "./UserDetail";
import UserFormModal from "./UserFormModal";
import { fullName, isActive, UsersTabProps } from "./userUtils";

// Pestaña Usuarios: tabla paginada en el servidor (GET /employee),
// alta/edición en modal, interruptor de activo y borrado.

// Columnas que el back puede ordenar (order_by); el rol se ordena por id_rol
const ORDER_BY: Record<string, EmployeeOrderBy> = {
  name: "name",
  email: "email",
  rol_name: "id_rol",
  active: "active",
  created_at: "created_at",
};

const ACTIVE_OPTIONS = [
  { value: "true", label: "Activos" },
  { value: "false", label: "Inactivos" },
];

type EmployeePage = { items: any[]; total: number; pages: number };

export default function UsersTab(props: UsersTabProps) {
  const { selfId, createOpen, onCreateClose, onSelfChanged } = props;

  const { data: roles } = useApiData<any[]>(getRoles);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [filterValues, setFilterValues] = useState<FilterValues>({});
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [result, setResult] = useState<EmployeePage>({ items: [], total: 0, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [toDeactivate, setToDeactivate] = useState<any>(null);
  const [toDelete, setToDelete] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  // Tras crear, editar o borrar se recarga la tabla
  const reload = () => setReloadKey((key) => key + 1);

  // Cualquier cambio de filtro vuelve a la primera página
  useEffect(() => setPage(0), [debouncedSearch]);

  useEffect(() => {
    let active = true;
    const sort = sortModel[0];
    setLoading(true);
    getEmployees({
      search: debouncedSearch.trim() || undefined,
      id_rol: filterValues.rol?.value,
      active: filterValues.active?.value,
      order_by: sort ? ORDER_BY[sort.field] : undefined,
      order_dir: sort?.sort ?? undefined,
      page: page + 1,
      page_size: pageSize,
    })
      .then((response) => {
        if (!active) return;
        const data: EmployeePage = response.data;
        // Si la página se ha quedado vacía (p. ej. tras borrar el último) se va a la última que exista
        if (data.items.length === 0 && page > 0 && page >= data.pages) {
          setPage(Math.max(0, data.pages - 1));
          return;
        }
        setResult(data);
      })
      .catch((error) => {
        if (active) handleApiError(error, { fallback: "No se han podido cargar los usuarios" });
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [debouncedSearch, filterValues, page, pageSize, sortModel, reloadKey]);

  const changeActive = (row: any, active: boolean) => {
    setSaving(true);
    setEmployeeActive(row.id, active)
      .then(() => {
        toast.success(active ? "Usuario activado" : "Usuario desactivado");
        setToDeactivate(null);
        setToDelete(null);
        reload();
      })
      .catch((error) =>
        handleApiError(error, { fallback: "No se ha podido cambiar el estado del usuario", onNotFound: reload })
      )
      .finally(() => setSaving(false));
  };

  const remove = (row: any) => {
    setSaving(true);
    deleteEmployee(row.id)
      .then(() => {
        toast.success("Usuario eliminado");
        setToDelete(null);
        reload();
      })
      .catch((error) => {
        // 409 (tiene datos asociados): se muestra el motivo y el modal sigue abierto para poder desactivarlo
        handleApiError(error, { fallback: "No se ha podido eliminar el usuario", onNotFound: reload });
        if (apiErrorStatus(error) === 404) setToDelete(null);
      })
      .finally(() => setSaving(false));
  };

  const filters = useMemo(
    () => [
      // El filtrado lo hace el back, así que getValue no se usa
      selectFilter(
        "rol",
        "Rol",
        (roles ?? []).map((rol: any) => ({ value: String(rol.id), label: rol.name })),
        () => null
      ),
      selectFilter("active", "Estado", ACTIVE_OPTIONS, () => null),
    ],
    [roles]
  );

  const columns: GridColDef[] = [
    idColumn(),
    // Nombre y apellidos juntos; se ordena por nombre
    nameColumn("name", "Nombre", {
      valueGetter: (params) => [params.row.name, params.row.surname].filter(Boolean).join(" "),
    }),
    textColumn("email", "Email", { flex: 2, ...alignLeft }),
    textColumn("rol_name", "Rol"),
    {
      field: "active",
      headerName: "Activo",
      width: 110,
      renderCell: (params) => {
        const active = isActive(params.row);
        const isSelf = params.row.id === selfId;
        const title = isSelf ? "No puedes desactivar tu propio usuario" : active ? "Desactivar" : "Activar";
        return (
          <Tooltip title={title}>
            {/* span: el Tooltip necesita un elemento activo aunque el interruptor esté deshabilitado */}
            <span onClick={(e) => e.stopPropagation()}>
              <Switch
                id={`active-switch-${params.row.id}`}
                size="small"
                color="success"
                checked={active}
                disabled={isSelf || saving}
                onChange={() => (active ? setToDeactivate(params.row) : changeActive(params.row, true))}
                inputProps={{ "aria-label": title }}
              />
            </span>
          </Tooltip>
        );
      },
    },
    dateColumn("created_at", "Alta"),
    actionsColumn({
      onEdit: (row) => setEditingId(row.id),
      onDelete: (row) => setToDelete(row),
      canDelete: (row) => row.id !== selfId,
    }),
  ];

  const deleteButtons = [
    { content: "Cancelar", primary: false, action: () => setToDelete(null) },
    ...(toDelete && isActive(toDelete)
      ? [
          { content: "Eliminar", primary: false, action: () => remove(toDelete), disabled: saving },
          { content: "Desactivar", primary: true, action: () => changeActive(toDelete, false), disabled: saving },
        ]
      : [{ content: "Eliminar", primary: true, action: () => remove(toDelete), disabled: saving }]),
  ];

  return (
    <>
      <TableFilters
        search={search}
        onSearchChange={setSearch}
        filters={filters}
        values={filterValues}
        onFilterChange={(id, value) => {
          setFilterValues((current) => ({ ...current, [id]: value }));
          setPage(0);
        }}
        onClear={() => {
          setSearch("");
          setFilterValues({});
          setPage(0);
        }}
        resultCount={result.total}
        totalCount={result.total}
      />

      <div className="entityPage__table">
        <ListEditItems
          rows={result.items}
          columns={columns}
          onRowClick={(row) => setDetailId(row.id)}
          fillHeight
          server={{
            rowCount: result.total,
            page,
            pageSize,
            onPageChange: setPage,
            onPageSizeChange: (size) => {
              setPageSize(size);
              setPage(0);
            },
            sortModel,
            onSortModelChange: (model) => {
              setSortModel(model);
              setPage(0);
            },
            loading,
          }}
        />
      </div>

      {createOpen || editingId !== null ? (
        <UserFormModal
          employeeId={editingId}
          roles={roles}
          selfId={selfId}
          onClose={() => {
            setEditingId(null);
            onCreateClose();
          }}
          onChanged={(id) => {
            reload();
            if (id !== undefined && id === selfId) onSelfChanged();
          }}
        />
      ) : null}

      <CustomModal
        size="xl"
        show={detailId !== null}
        onChange={() => setDetailId(null)}
        title="Información del usuario"
        body={
          detailId !== null ? (
            <UserDetail
              employeeId={detailId}
              onNotFound={() => {
                setDetailId(null);
                reload();
              }}
            />
          ) : null
        }
      />

      <ConfirmModal
        show={toDeactivate !== null}
        title="Desactivar usuario"
        message={`${fullName(toDeactivate)} no podrá acceder a la aplicación hasta que se vuelva a activar.`}
        confirmLabel="Desactivar"
        onConfirm={() => changeActive(toDeactivate, false)}
        onCancel={() => setToDeactivate(null)}
        disabled={saving}
      />

      <CustomModal
        size="lg"
        show={toDelete !== null}
        onChange={() => setToDelete(null)}
        title="Eliminar usuario"
        body={
          <p className="paragraph-text aedasModal__text">
            ¿Quieres eliminar a {fullName(toDelete)}? Esta acción no se puede deshacer.
            {toDelete && isActive(toDelete)
              ? " Si solo quieres quitarle el acceso, es mejor desactivarlo: se conservan sus datos y se puede volver a activar."
              : null}
          </p>
        }
        buttons={deleteButtons}
      />
    </>
  );
}
